/**
 * Additional Fields Utility Functions
 *
 * Provides functions to parse, merge, and manipulate additional fields
 * from both customFields and legacy jsonAdditionalConfig.
 */

import { CustomField } from '../types/configTemplate';
import {
  FieldSource as SharedFieldSource,
  getTypeBadgeColor as sharedGetTypeBadgeColor,
  getSourceBadgeColor as sharedGetSourceBadgeColor,
} from './fieldTypes';

/**
 * Value types supported by additional fields (narrow type — only data types selectable here)
 */
export type ValueType = 'string' | 'number' | 'array' | 'json';

/**
 * Source of the field (re-exported from shared module)
 */
export type FieldSource = SharedFieldSource;

/**
 * Represents an additional field with full metadata
 */
export interface AdditionalField {
  name: string;
  value: string | number | boolean | string[] | Record<string, unknown>;
  valueType: ValueType;
  source: FieldSource;
  rawValue?: string | number | boolean;  // Original value for editing
}

/**
 * Detect value type from a value
 */
export function detectValueType(value: unknown): ValueType {
  if (value === null || value === undefined) {
    return 'string';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'object') {
    return 'json';
  }
  // Check if string looks like a number
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && !isNaN(Number(trimmed))) {
      return 'number';
    }
  }
  return 'string';
}

/**
 * Parse legacy jsonAdditionalConfig (array of JSON strings or dict) to key-value pairs
 */
export function parseJsonAdditionalConfig(
  config: Record<string, unknown> | Array<string> | null
): AdditionalField[] {
  if (!config) {
    return [];
  }

  const fields: AdditionalField[] = [];

  // Handle array of JSON strings (legacy format)
  if (Array.isArray(config)) {
    for (const item of config) {
      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          if (typeof parsed === 'object' && parsed !== null) {
            // Extract key-value pairs from the parsed JSON
            for (const [key, value] of Object.entries(parsed)) {
              fields.push({
                name: key,
                value: value as AdditionalField['value'],
                valueType: detectValueType(value),
                source: 'device',
                rawValue: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value),
              });
            }
          }
        } catch {
          // If not valid JSON, treat as a simple string entry
          // Try to parse as key=value format
          if (item.includes('=')) {
            const [key, ...rest] = item.split('=');
            const value = rest.join('=');
            fields.push({
              name: key.trim(),
              value: value.trim(),
              valueType: 'string',
              source: 'device',
              rawValue: value.trim(),
            });
          }
        }
      }
    }
    return fields;
  }

  // Handle object/dict format
  if (typeof config === 'object') {
    for (const [key, value] of Object.entries(config)) {
      fields.push({
        name: key,
        value: value as AdditionalField['value'],
        valueType: detectValueType(value),
        source: 'device',
        rawValue: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value),
      });
    }
  }

  return fields;
}

/**
 * Convert CustomField[] to AdditionalField[]
 */
export function convertCustomFields(
  fields: CustomField[],
  source: FieldSource = 'user'
): AdditionalField[] {
  return fields.map(field => {
    // Try to parse the value to detect its type
    let parsedValue: AdditionalField['value'] = field.value;
    let valueType: ValueType = 'string';

    if (field.value != null) {
      const strVal = String(field.value);
      if (typeof field.value === 'boolean') {
        parsedValue = field.value;
        valueType = 'string'; // display as string toggle
      } else if (typeof field.value === 'number') {
        parsedValue = field.value;
        valueType = 'number';
      } else if (typeof field.value === 'object') {
        parsedValue = field.value;
        valueType = Array.isArray(field.value) ? 'array' : 'json';
      } else if (strVal.startsWith('[') || strVal.startsWith('{')) {
        // Check if it's a JSON array or object string
        try {
          parsedValue = JSON.parse(strVal);
          valueType = Array.isArray(parsedValue) ? 'array' : 'json';
        } catch {
          // Keep as string
          parsedValue = strVal;
        }
      } else if (!isNaN(Number(strVal)) && strVal.trim() !== '') {
        // It's a number
        parsedValue = Number(strVal);
        valueType = 'number';
      }
    }

    return {
      name: field.name,
      value: parsedValue,
      valueType,
      source: (field.source as FieldSource) || source,
      rawValue: field.value,
    };
  });
}

/**
 * Merge customFields with parsed jsonAdditionalConfig
 * CustomFields take precedence over jsonAdditionalConfig
 */
export function mergeAdditionalFields(
  customFields: CustomField[],
  jsonAdditionalConfig: Record<string, unknown> | Array<string> | null,
  templateFields?: CustomField[]
): AdditionalField[] {
  const fieldMap = new Map<string, AdditionalField>();

  // First, add fields from jsonAdditionalConfig (lowest priority)
  const deviceFields = parseJsonAdditionalConfig(jsonAdditionalConfig);
  for (const field of deviceFields) {
    fieldMap.set(field.name, field);
  }

  // Then, add template fields (medium priority)
  if (templateFields) {
    const converted = convertCustomFields(templateFields, 'template');
    for (const field of converted) {
      fieldMap.set(field.name, field);
    }
  }

  // Finally, add user custom fields (highest priority)
  const userFields = convertCustomFields(customFields, 'user');
  for (const field of userFields) {
    fieldMap.set(field.name, field);
  }

  return Array.from(fieldMap.values());
}

/**
 * Convert comma-separated string to array
 */
export function parseArrayValue(input: string): string[] {
  if (!input || input.trim() === '') {
    return [];
  }
  return input.split(',').map(item => item.trim()).filter(item => item !== '');
}

/**
 * Format array for display as comma-separated string
 */
export function formatArrayValue(arr: unknown[]): string {
  if (!Array.isArray(arr)) {
    return '';
  }
  return arr.map(item => String(item)).join(', ');
}

/**
 * Format value for display based on type
 */
export function formatValueForDisplay(value: unknown, valueType: ValueType): string {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (valueType) {
    case 'array':
      if (Array.isArray(value)) {
        return formatArrayValue(value);
      }
      return String(value);
    case 'json':
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    case 'number':
      return String(value);
    default:
      return String(value);
  }
}

/**
 * Parse input value based on type
 */
export function parseInputValue(
  input: string,
  valueType: ValueType
): AdditionalField['value'] {
  switch (valueType) {
    case 'number':
      const num = Number(input);
      return isNaN(num) ? 0 : num;
    case 'array':
      return parseArrayValue(input);
    case 'json':
      try {
        return JSON.parse(input);
      } catch {
        return {};
      }
    default:
      return input;
  }
}

/**
 * Validate value based on type
 */
export function validateValue(
  input: string,
  valueType: ValueType
): { valid: boolean; error?: string } {
  switch (valueType) {
    case 'number':
      if (input.trim() === '' || isNaN(Number(input))) {
        return { valid: false, error: 'Must be a valid number' };
      }
      return { valid: true };
    case 'json':
      if (input.trim() === '') {
        return { valid: true };  // Empty is OK
      }
      try {
        JSON.parse(input);
        return { valid: true };
      } catch (e) {
        return { valid: false, error: 'Invalid JSON syntax' };
      }
    case 'array':
      // Array input is always valid (comma-separated strings)
      return { valid: true };
    default:
      return { valid: true };
  }
}

/**
 * Convert AdditionalField back to CustomField for saving
 */
export function toCustomField(field: AdditionalField): CustomField {
  let value: string;

  switch (field.valueType) {
    case 'array':
      value = JSON.stringify(field.value);
      break;
    case 'json':
      value = JSON.stringify(field.value);
      break;
    case 'number':
      value = String(field.value);
      break;
    default:
      value = String(field.value);
  }

  return {
    name: field.name,
    value,
  };
}

// Re-export shared badge color functions so existing imports don't break
export const getTypeBadgeColor = sharedGetTypeBadgeColor;
export const getSourceBadgeColor = sharedGetSourceBadgeColor;
